import { useRef, useState, type KeyboardEvent } from 'react';
import type { LocationStatus } from '../hooks/useViewerLocation';
import { findPlaces, type ChosenPlace } from '../lib/places';
import { Menu } from './Menu';

interface Props {
  place: ChosenPlace | null;
  /** Distances are measured from the visitor's own location. */
  followingLocation: boolean;
  status: LocationStatus;
  onChoose: (place: ChosenPlace) => void;
  onUseMyLocation: () => void;
}

const MENU_ID = 'place-menu';

function setMenuOpen(open: boolean) {
  const menu = document.getElementById(MENU_ID);
  if (menu instanceof HTMLDetailsElement) menu.open = open;
}

/** Opens the place picker from elsewhere on the page (e.g. the "pick a place" notice). */
export const openPlaceMenu = () => setMenuOpen(true);

function PinIcon() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false">
      <path
        d="M12 2.5a7 7 0 0 0-7 7c0 5.2 7 12 7 12s7-6.8 7-12a7 7 0 0 0-7-7zm0 9.6a2.6 2.6 0 1 1 0-5.2 2.6 2.6 0 0 1 0 5.2z"
        fill="currentColor"
      />
    </svg>
  );
}

function LocateIcon() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false">
      <circle cx="12" cy="12" r="6.5" fill="none" stroke="currentColor" strokeWidth="2" />
      <circle cx="12" cy="12" r="2.5" fill="currentColor" />
      <path d="M12 1.5v4M12 18.5v4M1.5 12h4M18.5 12h4" stroke="currentColor" strokeWidth="2" />
    </svg>
  );
}

const LOCATION_HINT: Partial<Record<LocationStatus, string>> = {
  locating: 'Finding you…',
  denied: 'Blocked in this browser’s site settings',
  unavailable: 'Not available on this device',
};

/**
 * Where "within … km" is measured from: the visitor's own location, or any town they pick (e.g.
 * their home town while travelling). Remembered in the browser.
 */
export function PlaceMenu({ place, followingLocation, status, onChoose, onUseMyLocation }: Props) {
  const panel = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const latest = useRef('');
  const [query, setQuery] = useState('');
  const [found, setFound] = useState<{ query: string; places: ChosenPlace[] }>({
    query: '',
    places: [],
  });

  async function search(q: string) {
    setQuery(q);
    latest.current = q;
    const places = await findPlaces(q);
    if (latest.current === q) setFound({ query: q, places });
  }

  function choose(p: ChosenPlace) {
    onChoose(p);
    setMenuOpen(false);
  }

  // Arrow keys move between the search box and the choices; Enter in the box takes the first.
  function onKeyDown(e: KeyboardEvent) {
    if (e.key === 'Enter' && e.target === input.current) {
      e.preventDefault();
      if (found.query === query && found.places[0]) choose(found.places[0]);
      return;
    }
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    const items = [
      input.current,
      ...(panel.current?.querySelectorAll<HTMLElement>('.place-option') ?? []),
    ].filter((el): el is HTMLElement => !!el);
    const at = items.indexOf(document.activeElement as HTMLElement);
    const next =
      items[Math.max(0, Math.min(items.length - 1, at + (e.key === 'ArrowDown' ? 1 : -1)))];
    if (next) {
      e.preventDefault();
      next.focus();
    }
  }

  const name = place?.name ?? (followingLocation ? 'Near me' : 'Choose a place');
  const isCurrent = (p: ChosenPlace) =>
    !!place && place.name === p.name && place.lat === p.lat && place.lng === p.lng;
  const searched = found.query === query && query.trim().length >= 2;

  return (
    <Menu
      id={MENU_ID}
      label={
        place || followingLocation
          ? `Posts around ${place ? place.name : 'your location'}. Choose another place`
          : 'Choose a place'
      }
      summary={
        <>
          {place || !followingLocation ? <PinIcon /> : <LocateIcon />}
          <span className="place-menu__name">{name}</span>
        </>
      }
      className="place-menu"
      panelClassName="place-menu__panel"
      onToggle={(open) => {
        if (open) {
          input.current?.focus();
          return;
        }
        // Start fresh next time (cleared on close, so nothing typed right after opening is lost).
        setQuery('');
        setFound({ query: '', places: [] });
        latest.current = '';
      }}
    >
      <div ref={panel} onKeyDown={onKeyDown}>
        <p className="menu__title">Show posts around</p>
        <input
          ref={input}
          type="search"
          className="place-menu__search"
          placeholder="Search a city or town"
          aria-label="Search a city or town"
          autoComplete="off"
          spellCheck={false}
          enterKeyHint="go"
          value={query}
          onChange={(e) => void search(e.target.value)}
        />
        {found.places.length > 0 && (
          <div className="place-menu__options">
            {found.places.map((p) => (
              <button
                key={`${p.name}|${p.lat}|${p.lng}`}
                type="button"
                className="place-option"
                aria-pressed={isCurrent(p)}
                onClick={() => choose(p)}
              >
                <strong>{p.name}</strong>
                <span>{p.detail}</span>
              </button>
            ))}
          </div>
        )}
        {searched && found.places.length === 0 && (
          <p className="place-menu__none">No town found by that name.</p>
        )}
        <button
          type="button"
          className="place-option place-option--here"
          aria-pressed={followingLocation}
          onClick={onUseMyLocation}
        >
          <LocateIcon />
          <span>
            <strong>My location</strong>
            {LOCATION_HINT[status] && <span>{LOCATION_HINT[status]}</span>}
          </span>
        </button>
        <p className="place-menu__credit">
          Towns:{' '}
          <a href="https://www.geonames.org/" target="_blank" rel="noopener noreferrer">
            GeoNames
          </a>{' '}
          (CC BY 4.0)
        </p>
      </div>
    </Menu>
  );
}
